import { Component, input, output, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppSettings } from '@services/settings/app-settings.service';

export interface TimelineStep {
    id: number;
    title: string;
    icon?: string;
}

@Component({
    selector: 'app-timeline',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './timeline.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./timeline.component.css']
})
export class TimelineComponent {
    public appSettings = inject(AppSettings);
    steps = input<TimelineStep[]>([]);
    currentStep = input<number>(1);
    /** En revisión (bandeja) se puede saltar a cualquier paso. En alta, los futuros quedan bloqueados. */
    allowAllSteps = input<boolean>(false);
    stepChange = output<number>();

    currentStepIndex = computed(() => {
        const idx = this.steps().findIndex(s => s.id === this.currentStep());
        return idx >= 0 ? idx : 0;
    });

    currentStepTitle = computed(() => this.steps()[this.currentStepIndex()]?.title ?? '');
    currentStepIcon = computed(() => this.steps()[this.currentStepIndex()]?.icon ?? '');
    currentStepNumber = computed(() => this.currentStepIndex() + 1);
    totalSteps = computed(() => this.steps().length);
    canPrev = computed(() => this.currentStepIndex() > 0);
    canNext = computed(() => {
        const nextIdx = this.currentStepIndex() + 1;
        if (nextIdx >= this.totalSteps()) return false;
        const nextStep = this.steps()[nextIdx];
        return !!nextStep && !this.isDisabled(nextStep.id);
    });

    // Retrocede al paso anterior si está disponible
    prevStep(event?: Event): void {
        event?.preventDefault();
        if (!this.canPrev()) return;
        const target = this.steps()[this.currentStepIndex() - 1];
        if (target) this.stepChange.emit(target.id);
    }

    // Avanza al paso siguiente; el padre valida si aún no está desbloqueado
    nextStep(event?: Event): void {
        event?.preventDefault();
        if (!this.canNext()) return;
        const target = this.steps()[this.currentStepIndex() + 1];
        if (target) this.stepChange.emit(target.id);
    }

    onStepClick(stepId: number, event?: Event) {
        event?.preventDefault();
        if (this.isDisabled(stepId)) return;
        this.stepChange.emit(stepId);
    }

    isCompleted(stepId: number): boolean {
        return this.currentStep() > stepId;
    }

    isActive(stepId: number): boolean {
        return this.currentStep() === stepId;
    }

    isDisabled(stepId: number): boolean {
        if (this.allowAllSteps()) return false;
        return this.currentStep() < stepId;
    }
}
